import {
  SDKConfig,
  AuthorizationResponse,
  LearningBite,
  SDKError,
} from '../types';

/**
 * GiftFeud Learning Bite SDK
 * 
 * Official SDK for embedding GiftFeud learning bites in any application.
 * Handles rendering, progress tracking, and backend integration.
 * 
 * @example
 * ```typescript
 * const sdk = new GiftFeudSDK({
 *   apiKey: 'your-api-key',
 *   userToken: 'user-auth-token',
 *   environment: 'production',
 *   debug: true
 * });
 * 
 * const learningBite = await sdk.loadLearningBite('share-token-123');
 * ```
 */
export class GiftFeudSDK {
  private config: SDKConfig & { environment: string; baseUrl: string; domain: string; debug: boolean };
  private accessToken: string | null = null;
  private initialized: boolean = false;
  private userToken: string | null = null;

  constructor(config: SDKConfig) {
    this.config = {
      apiKey: config.apiKey,
      apiSecret: config.apiSecret || undefined,
      environment: config.environment || 'production',
      baseUrl: config.baseUrl || this.getDefaultBaseUrl(config.environment),
      domain: config.domain || (typeof window !== 'undefined' ? window.location.origin : ''),
      onError: config.onError || this.defaultErrorHandler,
      onTokenRefresh: config.onTokenRefresh || (() => {}),
      debug: config.debug || false,
      userToken: config.userToken || undefined,
    };

    this.userToken = config.userToken || null;
    this.log('SDK initialized');
  }

  /**
   * Initialize the SDK (authenticate with API)
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      await this.authorize();
      this.initialized = true;
      this.log('SDK ready');
    } catch (error) {
      this.handleError(error as Error, 'Failed to initialize');
      throw error;
    }
  }

  /**
   * Authorize SDK with GiftFeud API
   */
  private async authorize(shareToken?: string): Promise<AuthorizationResponse> {
    this.log('Authorizing...');

    const response = await fetch(`${this.config.baseUrl}/api/sdk/authorize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        apiKey: this.config.apiKey,
        apiSecret: this.config.apiSecret,
        domain: this.config.domain,
        shareToken,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Authorization failed' }));
      throw this.createError('AUTHORIZATION_FAILED', error.message, response.status, error);
    }

    const data: AuthorizationResponse = await response.json();
    this.accessToken = data.accessToken;
    
    return data;
  }

  /**
   * Load a learning bite by its share token
   * 
   * @param shareToken - The share token for the learning bite
   * @param pin - Optional PIN if the learning bite is protected
   * @returns Promise resolving to the learning bite data
   */
  async loadLearningBite(shareToken: string, pin?: string): Promise<LearningBite> {
    if (!this.initialized) {
      await this.initialize();
    }

    this.log('Loading learning bite:', shareToken);

    try {
      // Authorize with share token
      const authResponse = await this.authorize(shareToken);

      if (authResponse.learningBite) {
        return authResponse.learningBite;
      }

      // Fallback: fetch separately
      return await this.fetchLearningBite(shareToken, pin);
    } catch (error) {
      this.handleError(error as Error, 'Failed to load');
      throw error;
    }
  }

  /**
   * Fetch learning bite data from API
   */
  private async fetchLearningBite(shareToken: string, pin?: string): Promise<LearningBite> {
    const response = await fetch(`${this.config.baseUrl}/shared-learning/${shareToken}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.accessToken && { Authorization: `Bearer ${this.accessToken}` }),
      },
      body: JSON.stringify({ pin }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch' }));
      throw this.createError('FETCH_FAILED', error.message, response.status, error);
    }

    const data = await response.json();
    this.log('Raw API response for learning bite:', data);
    this.log('First slide raw data:', data.slides?.[0]);
    return data;
  }

  /**
   * Progress Tracking: Start tracking when user opens learning bite
   */
  async startProgress(learningElementId: string, campaignId?: string): Promise<any> {
    if (!this.userToken) {
      this.log('Warning: No user token provided, progress will not be tracked');
      return null;
    }

    this.log('Starting progress tracking for:', learningElementId);

    try {
      const response = await fetch(
        `${this.config.baseUrl}/learning-elements/${learningElementId}/progress/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.userToken}`,
          },
          body: JSON.stringify({ campaignId }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to start progress');
      }

      return response.json();
    } catch (error) {
      this.handleError(error as Error, 'Failed to start progress');
      return null;
    }
  }

  /**
   * Progress Tracking: Submit user's answer to a question
   */
  async submitAnswer(
    learningElementId: string,
    slideId: string,
    answer: any,
    campaignId?: string
  ): Promise<any> {
    if (!this.userToken) {
      this.log('Warning: No user token provided, answer will not be saved');
      return null;
    }

    this.log('Submitting answer for slide:', slideId);

    try {
      // Build URL with campaignId as query parameter if provided
      const url = new URL(`${this.config.baseUrl}/learning-elements/${learningElementId}/progress/answer`);
      if (campaignId) {
        url.searchParams.append('campaignId', campaignId);
      }

      const response = await fetch(
        url.toString(),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.userToken}`,
          },
          body: JSON.stringify({
            slideId,
            answer,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit answer');
      }

      return response.json();
    } catch (error) {
      this.handleError(error as Error, 'Failed to submit answer');
      return null;
    }
  }

  /**
   * Progress Tracking: Mark a slide as completed
   */
  async completeSlide(
    learningElementId: string,
    slideId: string,
    timeSpent: number,
    campaignId?: string
  ): Promise<any> {
    if (!this.userToken) {
      this.log('Warning: No user token provided, slide completion will not be tracked');
      return null;
    }

    this.log('Completing slide:', slideId);

    try {
      // Build URL with campaignId as query parameter if provided
      const url = new URL(`${this.config.baseUrl}/learning-elements/${learningElementId}/progress/complete-slide`);
      if (campaignId) {
        url.searchParams.append('campaignId', campaignId);
      }

      const response = await fetch(
        url.toString(),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.userToken}`,
          },
          body: JSON.stringify({
            slideId,
            timeSpentSeconds: timeSpent,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to complete slide');
      }

      return response.json();
    } catch (error) {
      this.handleError(error as Error, 'Failed to complete slide');
      return null;
    }
  }

  /**
   * Progress Tracking: Update media playback progress
   */
  async updateMediaProgress(
    learningElementId: string,
    slideId: string,
    maxPlaybackPositionSeconds: number,
    mediaDurationSeconds: number,
    campaignId?: string
  ): Promise<any> {
    if (!this.userToken) {
      this.log('Warning: No user token provided, media progress will not be tracked');
      return null;
    }

    this.log('Updating media progress:', { slideId, position: maxPlaybackPositionSeconds, duration: mediaDurationSeconds });

    try {
      // Build URL with campaignId as query parameter if provided
      const url = new URL(`${this.config.baseUrl}/learning-elements/${learningElementId}/progress/media-progress`);
      if (campaignId) {
        url.searchParams.append('campaignId', campaignId);
      }

      const response = await fetch(
        url.toString(),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.userToken}`,
          },
          body: JSON.stringify({
            slideId,
            maxPlaybackPositionSeconds: Math.round(maxPlaybackPositionSeconds),
            mediaDurationSeconds: Math.round(mediaDurationSeconds),
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update media progress');
      }

      return response.json();
    } catch (error) {
      this.handleError(error as Error, 'Failed to update media progress');
      return null;
    }
  }

  /**
   * Progress Tracking: Mark entire learning element as completed
   */
  async complete(
    learningElementId: string,
    totalTime: number,
    campaignId?: string
  ): Promise<any> {
    if (!this.userToken) {
      this.log('Warning: No user token provided, completion will not be tracked');
      return null;
    }

    this.log('Completing learning element:', learningElementId);

    try {
      const response = await fetch(
        `${this.config.baseUrl}/learning-elements/${learningElementId}/progress/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.userToken}`,
          },
          body: JSON.stringify({
            totalTimeSpentSeconds: totalTime,
            campaignId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to complete learning element');
      }

      return response.json();
    } catch (error) {
      this.handleError(error as Error, 'Failed to complete learning element');
      return null;
    }
  }

  /**
   * Update user token (for authentication changes)
   */
  setUserToken(token: string | null): void {
    this.userToken = token;
    this.log('User token updated');
  }

  /**
   * Get default API base URL based on environment
   */
  private getDefaultBaseUrl(environment?: string): string {
    return environment === 'development'
      ? 'http://localhost:3000'
      : 'https://api.giftfeud.com';
  }

  /**
   * Create SDK error object
   */
  private createError(code: string, message: string, statusCode?: number, details?: any): SDKError {
    const error = new Error(message) as SDKError;
    error.code = code;
    error.statusCode = statusCode;
    error.details = details;
    return error;
  }

  /**
   * Handle errors internally
   */
  private handleError(error: Error, context: string): void {
    this.log('ERROR:', context, error);
    const errorHandler = this.config.onError || this.defaultErrorHandler;
    errorHandler(error as SDKError);
  }

  /**
   * Default error handler
   */
  private defaultErrorHandler(error: SDKError): void {
    console.error('[GiftFeud SDK]', error);
  }

  /**
   * Internal logging (only when debug mode is enabled)
   */
  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[GiftFeud SDK]', ...args);
    }
  }
}

